// apps/web/src/admin/UserDetailDrawer.tsx
import { useEffect, useState } from 'react';
import { apiAdmin } from './apiAdmin.js';
import { DocPreviewModal } from './DocPreviewModal.js';
import { PurgeConfirmModal } from './PurgeConfirmModal.js';

interface Props { userId: string; onClose: () => void; onChange: () => void; }

const TABS: [Tab, string][] = [['calls', 'calls'], ['billing', 'billing'], ['logins', 'logins'], ['audit', 'audit'], ['docs', 'intro/resume']];
type Tab = 'calls' | 'billing' | 'logins' | 'audit' | 'docs';

interface BillingRow {
  id: number;
  kind: string;
  minutes_delta: number;
  dollars: number | null;
  note: string | null;
  session_id: number | null;
  reverses_id: number | null;
  created_at: string;
}

export function UserDetailDrawer({ userId, onClose, onChange }: Props) {
  const [data, setData] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);
  const [tab, setTab]   = useState<Tab>('calls');
  const [preview, setPreview] = useState<{ url: string; kind: 'video' | 'resume' } | null>(null);
  const [purging, setPurging] = useState(false);

  async function reload() { setData(await apiAdmin.userDetail(userId)); }
  useEffect(() => { void reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [userId]);

  async function openDoc(kind: 'video' | 'resume') {
    setErr(null);
    try { const { url } = await apiAdmin.guideDocumentUrl(userId, kind); setPreview({ url, kind }); }
    catch (e: any) { setErr(e.message ?? String(e)); }
  }

  async function run<T>(fn: () => Promise<T>) {
    setBusy(true); setErr(null);
    try { await fn(); await reload(); onChange(); }
    catch (e: any) { setErr(e.message ?? String(e)); }
    finally { setBusy(false); }
  }

  function confirm(action: string, fn: () => Promise<void>) {
    if (window.confirm(`Confirm ${action} for ${data.user.email}?`)) void run(fn);
  }

  function reverseEntry(eventId: number) {
    const reason = window.prompt('Reason for reversing this entry?')?.trim();
    if (!reason) return;
    void run(async () => { await apiAdmin.reverse(u.id, { eventId, reason }); });
  }

  function reverseSession(sessionId: number) {
    const reason = window.prompt('Reason for reversing this session?')?.trim();
    if (!reason) return;
    void run(async () => { await apiAdmin.reverseSession(u.id, { sessionId, reason }); });
  }

  if (!data) return null;
  const u = data.user;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <aside className="w-[520px] bg-canvas-light dark:bg-canvas-dark border-l border-ink-200 dark:border-white/10 p-5 overflow-y-auto">
        <button onClick={onClose} className="text-sm text-ink-500">Close</button>
        <h2 className="text-xl font-semibold mt-2">{u.displayName}</h2>
        <p className="text-ink-500 text-sm">{u.email} · {u.role}{u.isAdmin ? ' · admin' : ''}{u.disabledAt ? ' · disabled' : ''}</p>

        <div className="mt-4 flex flex-wrap gap-2">
          {u.isAdmin
            ? <button disabled={busy} className="btn" onClick={() => confirm('revoke admin', async () => { await apiAdmin.revoke(u.id); })}>Revoke admin</button>
            : <button disabled={busy} className="btn" onClick={() => confirm('promote to admin', async () => { await apiAdmin.promote(u.id); })}>Promote to admin</button>}
          {u.disabledAt
            ? <button disabled={busy} className="btn" onClick={() => confirm('enable account', async () => { await apiAdmin.enable(u.id); })}>Enable account</button>
            : <button disabled={busy} className="btn" onClick={() => {
                const reason = window.prompt('Reason for disabling?')?.trim();
                if (!reason) return;
                void run(async () => { await apiAdmin.disable(u.id, { reason }); });
              }}>Disable account</button>}
          {u.role === 'learner' ? (
            <button disabled={busy} className="btn" onClick={() => {
              const delta = Number(window.prompt('Delta minutes (negative to subtract)?'));
              if (!Number.isInteger(delta) || delta === 0) return;
              const reason = window.prompt('Reason?')?.trim();
              if (!reason) return;
              void run(async () => { await apiAdmin.adjust(u.id, { deltaMinutes: delta, reason }); });
            }}>Adjust balance</button>
          ) : null}
          <button disabled={busy} className="btn bg-red-600 text-white ml-auto"
            onClick={() => { setErr(null); setPurging(true); }}>
            Purge account
          </button>
        </div>

        {err ? <p className="text-red-600 text-sm mt-2">{err}</p> : null}

        <div className="mt-6 border-b border-ink-200 dark:border-white/10 flex gap-4 text-sm">
          {TABS.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`pb-2 -mb-px border-b-2 ${tab === key ? 'border-signwow text-ink-900 dark:text-ink-100' : 'border-transparent text-ink-500'}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="mt-3 text-sm">
          {tab === 'calls'   ? <pre className="text-xs overflow-x-auto">{JSON.stringify(data.calls,   null, 2)}</pre> : null}
          {tab === 'billing' ? (() => {
            const billing = data.billing as BillingRow[];
            const reversedIds = new Set(billing.map((r) => r.reverses_id).filter((id): id is number => id != null));

            // 'consume' rows sharing a session_id are the many-rows-per-minute
            // ticks apply_consume writes for one call. Group them into a single
            // line with one Reverse session control; every other kind (and a
            // consume with a NULL session_id — can't be grouped) keeps
            // rendering as its own row with the existing per-entry control.
            const consumeGroups = new Map<number, BillingRow[]>();
            for (const row of billing) {
              if (row.kind === 'consume' && row.session_id != null) {
                const arr = consumeGroups.get(row.session_id) ?? [];
                arr.push(row);
                consumeGroups.set(row.session_id, arr);
              }
            }
            const renderedSessions = new Set<number>();

            return billing.length === 0 ? <p className="text-ink-500">No billing events.</p> : (
              <div className="flex flex-col gap-1">
                {billing.map((row) => {
                  if (row.kind === 'consume' && row.session_id != null) {
                    if (renderedSessions.has(row.session_id)) return null;
                    renderedSessions.add(row.session_id);
                    const group = consumeGroups.get(row.session_id)!;
                    const totalMinutes = group.reduce((sum, r) => sum + Math.abs(r.minutes_delta), 0);
                    const totalDollars = group.reduce((sum, r) => sum + (r.dollars ?? 0), 0);
                    const allReversed = group.every((r) => reversedIds.has(r.id));
                    return (
                      <div key={`session-${row.session_id}`} className="flex items-center gap-3 border-b border-ink-100 dark:border-white/5 py-1 text-xs">
                        <span className="text-ink-500 whitespace-nowrap">{new Date(row.created_at).toLocaleString()}</span>
                        <span className="whitespace-nowrap">session {row.session_id}</span>
                        <span className="whitespace-nowrap">{totalMinutes}m</span>
                        <span className="whitespace-nowrap">${totalDollars}</span>
                        <span className="flex-1 truncate text-ink-500">{group.length} tick{group.length === 1 ? '' : 's'}</span>
                        {!allReversed ? (
                          <button disabled={busy} className="btn shrink-0" onClick={() => reverseSession(row.session_id!)}>Reverse session</button>
                        ) : null}
                      </div>
                    );
                  }

                  const canReverse = row.kind !== 'failed' && row.kind !== 'dispute' && !reversedIds.has(row.id);
                  return (
                    <div key={row.id} className="flex items-center gap-3 border-b border-ink-100 dark:border-white/5 py-1 text-xs">
                      <span className="text-ink-500 whitespace-nowrap">{new Date(row.created_at).toLocaleString()}</span>
                      <span className="whitespace-nowrap">{row.kind}</span>
                      <span className="whitespace-nowrap">{row.minutes_delta}m</span>
                      <span className="whitespace-nowrap">${row.dollars ?? 0}</span>
                      <span className="flex-1 truncate text-ink-500">{row.note}</span>
                      {canReverse ? (
                        <button disabled={busy} className="btn shrink-0" onClick={() => reverseEntry(row.id)}>Reverse</button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            );
          })() : null}
          {tab === 'logins'  ? <pre className="text-xs overflow-x-auto">{JSON.stringify(data.logins,  null, 2)}</pre> : null}
          {tab === 'audit'   ? <pre className="text-xs overflow-x-auto">{JSON.stringify(data.audit,   null, 2)}</pre> : null}
          {tab === 'docs' ? (
            u.role !== 'guide'
              ? <p className="text-ink-500">This user is not a guide — no intro video or résumé.</p>
              : (
                <div className="flex flex-col items-start gap-2">
                  {data.guideDocs?.hasVideo
                    ? <button className="btn" onClick={() => void openDoc('video')}>▶ Play intro video</button>
                    : <p className="text-ink-500">No intro video uploaded.</p>}
                  {data.guideDocs?.hasResume
                    ? <button className="btn" onClick={() => void openDoc('resume')}>View / download résumé</button>
                    : <p className="text-ink-500">No résumé uploaded.</p>}
                </div>
              )
          ) : null}
        </div>
      </aside>
      {purging ? (
        <PurgeConfirmModal
          email={u.email}
          isGuide={u.role === 'guide'}
          busy={busy}
          error={err}
          onCancel={() => setPurging(false)}
          onConfirm={(reason) => {
            void (async () => {
              setBusy(true); setErr(null);
              try { await apiAdmin.purge(u.id, { reason }); setPurging(false); onChange(); onClose(); }
              catch (e: any) { setErr(e.message ?? String(e)); }
              finally { setBusy(false); }
            })();
          }}
        />
      ) : null}
      {preview ? <DocPreviewModal url={preview.url} kind={preview.kind} onClose={() => setPreview(null)} /> : null}
    </div>
  );
}
